import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Modal, ModalDialog,
    ModalClose, Divider, Avatar, Tooltip, Checkbox,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import AddRoundedIcon           from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon        from '@mui/icons-material/DeleteRounded';
import SearchRoundedIcon        from '@mui/icons-material/SearchRounded';
import ReceiptLongRoundedIcon   from '@mui/icons-material/ReceiptLongRounded';
import FilterListRoundedIcon    from '@mui/icons-material/FilterListRounded';
import ArrowBackRoundedIcon     from '@mui/icons-material/ArrowBackRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import CreditCardRoundedIcon    from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon      from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon   from '@mui/icons-material/PointOfSaleRounded';

import DateQuickPicker from '../../components/DateQuickPicker';
import UndoToast from '../../components/UndoToast';
import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetOrders, APIDeleteOrder, APIBulkDeleteOrders,
    APIGetEmployees, APIGetLocations,
    parseApiError,
    API_URL,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import useMultiSelect from '../../hooks/useMultiSelect';
import SelectionToolbar from '../../components/SelectionToolbar';
import { BankBadge } from '../../components/BankBrand';



// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const businessToday = () => { const d = new Date(); d.setHours(d.getHours() - 6); return toISO(d); };
const businessYesterday = () => { const d = new Date(); d.setHours(d.getHours() - 6); d.setDate(d.getDate() - 1); return toISO(d); };

const getMonthStart = () => {
    const now = new Date();
    return toISO(new Date(now.getFullYear(), now.getMonth(), 1));
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

const MONEY_ICONS = {
    transfer: <CreditCardRoundedIcon sx={{ fontSize: 14 }} />,
    cash:     <PaymentsRoundedIcon   sx={{ fontSize: 14 }} />,
    terminal: <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} />,
};

const MONEY_LABELS = {
    transfer: 'Перевод',
    cash:     'Наличные',
    terminal: 'Терминал',
};

const MONEY_COLORS = {
    transfer: { color: '#818cf8', bg: 'rgba(129,140,248,0.10)' },
    cash:     { color: '#22c55e', bg: 'rgba(34,197,94,0.10)' },
    terminal: { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.04 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};



// ════════════════════════════════════════════════════════════════════
//  OrdersPage
// ════════════════════════════════════════════════════════════════════
export default function OrdersPage() {
    const { token, showToast, onRefreshTick, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canCreate = hasPerm('finance.orders.create');
    const canDelete = hasPerm('finance.orders.delete');

    // ── Данные ─────────────────────────────────────────────────────
    const [orders,        setOrders]        = useState([]);
    const [employees,     setEmployees]     = useState([]);
    const [locations,     setLocations]     = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [undoData,      setUndoData]           = useState(null); // { ids: [1,2,3], message: '3 чека добавлено' }

    // ── Фильтры ───────────────────────────────────────────────────
    const [dateFrom,      setDateFrom]      = useState(getMonthStart);
    const [dateTo,        setDateTo]        = useState(() => businessToday());
    const [filterPhoto,   setFilterPhoto]   = useState('');
    const [filterLoc,     setFilterLoc]     = useState('');
    const [filterMoney,   setFilterMoney]   = useState('');
    const [search,        setSearch]        = useState('');
    const [showFilters,   setShowFilters]   = useState(false);
    const [activeQuick, setActiveQuick] = useState('');

    // ── Удаление ──────────────────────────────────────────────────
    const [deleteTarget,  setDeleteTarget]  = useState(null);
    const [deleting,      setDeleting]      = useState(false);

    const sel = useMultiSelect();
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const handleBulkDelete = async () => {
        if (sel.count === 0) return;
        if (!window.confirm(`Удалить выбранные чеки (${sel.count})? Выручка пересчитается. Действие необратимо.`)) return;
        setBulkDeleting(true);
        const res = await APIBulkDeleteOrders(token, sel.ids);
        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            showToast(`Удалено чеков: ${res.deleted ?? sel.ids.length}`);
            sel.exitSelectMode();
            await loadOrders();
        }
        setBulkDeleting(false);
    };

    // ── Загрузка ──────────────────────────────────────────────────
    const loadOrders = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo)   params.date_to   = dateTo;
        if (filterPhoto) params.photographer_id = filterPhoto;
        if (filterLoc)   params.location_id     = filterLoc;

        const data = await APIGetOrders(token, params);
        if (Array.isArray(data)) setOrders(data);
        setLoading(false);
    }, [token, dateFrom, dateTo, filterPhoto, filterLoc]);

    const loadMeta = useCallback(async () => {
        const [empl, locs] = await Promise.all([
            APIGetEmployees(token, 'photographer'),
            APIGetLocations(token),
        ]);
        if (Array.isArray(empl)) setEmployees(empl);
        if (Array.isArray(locs)) setLocations(locs);
    }, [token]);

    useEffect(() => { loadMeta(); }, [loadMeta]);
    useEffect(() => { loadOrders(); }, [loadOrders]);
    useEffect(() => onRefreshTick(loadOrders), [onRefreshTick, loadOrders]);

    // ── Удаление ──────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        const res = await APIDeleteOrder(token, deleteTarget.id);
        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            showToast('Чек удалён');
            // Сервер удаляет ВЕСЬ чек (все строки одного receipt_uid), поэтому
            // фильтровать локально по одному id недостаточно — перезапрашиваем список.
            await loadOrders();
        }
        setDeleting(false);
        setDeleteTarget(null);
    };

    // ── Фильтрация + поиск на клиенте ────────────────────────────
    const filtered = useMemo(() => {
        let list = orders;
        if (filterMoney) list = list.filter(o => o.money_type === filterMoney);
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(o =>
                (o.photographer_name || '').toLowerCase().includes(q) ||
                (o.location_name || '').toLowerCase().includes(q) ||
                String(o.value).includes(q)
            );
        }
        return list;
    }, [orders, filterMoney, search]);

    // ── Итоги ─────────────────────────────────────────────────────
    const totals = useMemo(() => {
        const transfer = filtered.filter(o => o.money_type === 'transfer').reduce((s, o) => s + parseFloat(o.value || 0), 0);
        const cash     = filtered.filter(o => o.money_type === 'cash').reduce((s, o) => s + parseFloat(o.value || 0), 0);
        const terminal = filtered.filter(o => o.money_type === 'terminal').reduce((s, o) => s + parseFloat(o.value || 0), 0);
        return { transfer, cash, terminal, total: transfer + cash + terminal, count: filtered.length };
    }, [filtered]);

    // ── Группировка по дате ───────────────────────────────────────
    // Внутри дня сохраняем порядок с сервера: он отсортирован по
    // COALESCE(receipt_datetime, date_create) DESC — то есть свежие сверху
    // по точному моменту чека, а не просто по дате.
    const grouped = useMemo(() => {
        const map = {};
        for (const o of filtered) {
            const key = o.date || 'unknown';
            if (!map[key]) map[key] = [];
            map[key].push(o);
        }
        // Дни сортируем по убыванию (свежие сверху)
        return Object.entries(map).sort((a, b) => (b[0] || '').localeCompare(a[0] || ''));
    }, [filtered]);

    // Выбираем из видимого (отфильтрованного) списка чеков.
    const selectableIds = useMemo(
        () => filtered.map(o => o.id),
        [filtered],
    );

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ──────────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton variant="plain" onClick={() => navigate('/manager')}
                                sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                            <Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Чеки
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                    Управление продажами фотографов
                                </Typography>
                            </Box>
                        </Box>
                        {canCreate && (
                            <Button
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => navigate('/manager/orders/add')}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: 13, border: 'none',
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                    '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                }}
                            >
                                Добавить чек
                            </Button>
                        )}
                    </Box>
                </motion.div>

                {/* ── Итого-карточки ────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.08 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.5, mb: 2.5 }}>
                        {[
                            { label: 'Всего',     value: fmtMoney(totals.total),    sub: `${totals.count} чек.`, color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)', icon: <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} /> },
                            { label: 'Перевод',   value: fmtMoney(totals.transfer), color: '#818cf8', bg: 'rgba(129,140,248,0.08)', icon: <CreditCardRoundedIcon sx={{ fontSize: 18 }} /> },
                            { label: 'Наличные',  value: fmtMoney(totals.cash),     color: '#22c55e', bg: 'rgba(34,197,94,0.08)', icon: <PaymentsRoundedIcon sx={{ fontSize: 18 }} /> },
                            { label: 'Терминал',  value: fmtMoney(totals.terminal), color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: <PointOfSaleRoundedIcon sx={{ fontSize: 18 }} /> },
                        ].map(s => (
                            <Box key={s.label} sx={{ px: 2, py: 1.8, borderRadius: 'var(--joy-radius-lg)', border: '1px solid var(--joy-palette-divider)', bgcolor: s.bg }}>
                                <Box sx={{ color: s.color, mb: 0.5 }}>{s.icon}</Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>{s.label}</Typography>
                                {s.sub && <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>{s.sub}</Typography>}
                            </Box>
                        ))}
                    </Box>
                </motion.div>

                {/* ── Фильтры ──────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.12 }}>
                    <Box sx={{
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        p: { xs: 1.5, sm: 2 }, mb: 2.5,
                    }}>
                        {/* Строка поиска + кнопка фильтров */}
                        <Box sx={{ display: 'flex', gap: 1, mb: showFilters ? 1.5 : 0 }}>
                            <Input
                                placeholder="Поиск по имени, точке, сумме…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                startDecorator={<SearchRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-tertiary)' }} />}
                                size="sm"
                            />
                            <IconButton
                                variant={showFilters ? 'solid' : 'outlined'}
                                color={showFilters ? 'primary' : 'neutral'}
                                onClick={() => setShowFilters(p => !p)}
                                size="sm"
                                sx={{ 
                                    bgcolor: 'var(--joy-palette-background-level2)', 
                                    border: '1px solid var(--joy-palette-divider)',
                                    "&:hover": {
                                        bgcolor: 'var(--joy-palette-background-level3)', 
                                    }
                                }}
                            >
                                <FilterListRoundedIcon sx={{ fontSize: 18, color: 'white' }} />
                            </IconButton>
                        </Box>
                        
                        {/* Быстрые периоды */}
                        <Stack direction={"row"} sx={{ flexWrap: 'wrap', gap: 0.8, mb: 1.5, mt: 2 }}>
                            {[
                                { label: 'Сегодня', from: businessToday(), to: businessToday() },
                                { label: 'Вчера',   from: businessYesterday(), to: businessYesterday() },
                                { label: 'Неделя',  from: (() => { const d = new Date(); d.setHours(d.getHours()-6); d.setDate(d.getDate() - (d.getDay() === 0 ? 6 : d.getDay()-1)); return toISO(d); })(), to: businessToday() },
                                { label: 'Месяц',   from: getMonthStart(), to: businessToday() },
                            ].map(p => (
                                <Chip key={p.label} size="sm" variant="outlined" color="neutral"
                                    onClick={() => { setDateFrom(p.from); setDateTo(p.to); setShowFilters(true); }}
                                    sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 12, px: 2, py: 0.5, 
                                        "&:hover": {
                                            color: 'black'
                                        }
                                    }}
                                >
                                    {p.label}
                                </Chip>
                            ))}
                        </Stack>

                        {/* Развёрнутые фильтры */}
                        <AnimatePresence>
                            {showFilters && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25 }}
                                    style={{ overflow: 'hidden' }}
                                >
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr 1fr' }, gap: 1 }}>
                                        {/* <Box sx={{ mb: 1.5 }}>
                                            <DateQuickPicker
                                                activeKey={activeQuick}
                                                onChange={(from, to, key) => {
                                                    setActiveQuick(key);
                                                    setDateFrom(from);
                                                    setDateTo(to);
                                                }}
                                            />
                                        </Box> */}
                                        <Input type="date" size="sm" value={dateFrom}
                                            onChange={(e) => { setDateFrom(e.target.value); setActiveQuick(''); }}
                                            slotProps={{ input: { 'aria-label': 'Дата от' } }}
                                        />
                                        <Input type="date" size="sm" value={dateTo}
                                            onChange={(e) => { setDateFrom(e.target.value); setActiveQuick(''); }}
                                            slotProps={{ input: { 'aria-label': 'Дата до' } }}
                                        />
                                        <Select size="sm" value={filterPhoto} onChange={(_, v) => setFilterPhoto(v || '')}
                                            placeholder="Фотограф"
                                        >
                                            <Option value="">Все фотографы</Option>
                                            {employees.map(e => (
                                                <Option key={e.id} value={e.id}>{e.first_name} {e.last_name}</Option>
                                            ))}
                                        </Select>
                                        <Select size="sm" value={filterLoc} onChange={(_, v) => setFilterLoc(v || '')}
                                            placeholder="Точка"
                                        >
                                            <Option value="">Все точки</Option>
                                            {locations.map(l => (
                                                <Option key={l.id} value={l.id}>{l.name}</Option>
                                            ))}
                                        </Select>
                                        <Select size="sm" value={filterMoney} onChange={(_, v) => setFilterMoney(v || '')}
                                            placeholder="Тип оплаты"
                                        >
                                            <Option value="">Все типы</Option>
                                            <Option value="transfer">Перевод</Option>
                                            <Option value="cash">Наличные</Option>
                                            <Option value="terminal">Терминал</Option>
                                        </Select>
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>
                </motion.div>

                {/* ── Список чеков ──────────────────────────────── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <ReceiptLongRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            Нет чеков за выбранный период
                        </Typography>
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2}>

                            {canDelete && selectableIds.length > 0 && (
                                <SelectionToolbar
                                    selectMode={sel.selectMode}
                                    onEnter={() => sel.setSelectMode(true)}
                                    onExit={sel.exitSelectMode}
                                    count={sel.count}
                                    totalSelectable={selectableIds.length}
                                    allChecked={selectableIds.length > 0 && sel.count === selectableIds.length}
                                    onToggleAll={(e) => e.target.checked ? sel.selectAll(selectableIds) : sel.clear()}
                                    onDelete={handleBulkDelete}
                                    deleting={bulkDeleting}
                                    enterLabel="Выбрать несколько"
                                    deleteLabel="Удалить чеки"
                                />
                            )}

                            {grouped.map(([date, items]) => (
                                <motion.div key={date} variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        overflow: 'hidden',
                                    }}>
                                        {/* Заголовок дня */}
                                        <Box sx={{
                                            px: 2, py: 1.2,
                                            borderBottom: '1px solid var(--joy-palette-divider)',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {fmtDate(date)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                                {items.length} чек. · {fmtMoney(items.reduce((s, o) => s + parseFloat(o.value || 0), 0))} ₽
                                            </Typography>
                                        </Box>

                                        {/* Чеки за день */}
                                        <Stack spacing={0}>
                                            {items.map((order, idx) => {
                                                const mc = MONEY_COLORS[order.money_type] || MONEY_COLORS.transfer;
                                                return (
                                                    <Box key={order.id} sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                                        px: 2, py: 1.2,
                                                        borderBottom: idx < items.length - 1
                                                            ? '1px solid var(--joy-palette-divider)' : 'none',
                                                        transition: 'background .15s',
                                                        '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                                                    }}>
                                                        {sel.selectMode && canDelete && (
                                                            <Checkbox size="sm"
                                                                checked={sel.isSelected(order.id)}
                                                                onChange={() => sel.toggle(order.id)}
                                                                sx={{ mr: 0.5, flexShrink: 0 }} />
                                                        )}

                                                        {/* Аватар фотографа */}
                                                        <Avatar src={order.photographer_avatar_url} size="sm" sx={{
                                                            width: 32, height: 32, fontSize: 11, fontWeight: 700,
                                                            bgcolor: 'rgba(162,38,226,0.15)',
                                                            color: 'var(--joy-palette-primary-300)',
                                                            border: '1.5px solid var(--joy-palette-primary-500)',
                                                            flexShrink: 0,
                                                        }}>
                                                            {(order.photographer_name || '').charAt(0).toUpperCase()}
                                                        </Avatar>

                                                        {/* Инфо */}
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography
                                                                onClick={() => order.photographer && setFilterPhoto(order.photographer)}
                                                                sx={{
                                                                fontSize: 13, fontWeight: 600,
                                                                color: 'var(--joy-palette-text-primary)',
                                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                                cursor: 'pointer',
                                                                '&:hover': { textDecoration: 'underline', color: 'var(--joy-palette-primary-400)' },
                                                            }}>
                                                                {order.photographer_name}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.3 }}>
                                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                    {order.location_name}
                                                                </Typography>
                                                                {order.administrator_name && (
                                                                    <>
                                                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>·</Typography>
                                                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                            Админ: {order.administrator_name}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            </Box>
                                                            {(() => {
                                                                const rdt = (order.receipt_datetime_effective || order.receipt_datetime)
                                                                    ? new Date(order.receipt_datetime_effective || order.receipt_datetime)
                                                                    : null;
                                                                const cdt = new Date(order.date_create);
                                                                // Время показываем всегда; «кто внёс» дописываем, если есть.
                                                                const showBoth = rdt && Math.abs(rdt - cdt) > 60_000;
                                                                const fmt = (d) => d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                                                                return (
                                                                    <Box sx={{ mt: 0.2 }}>
                                                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                            Чек: {fmt(rdt || cdt)}{order.created_by_name ? ` · ${order.created_by_name}` : ''}
                                                                        </Typography>
                                                                        {showBoth && (
                                                                            <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)', opacity: 0.7 }}>
                                                                                Оформлен: {fmt(cdt)}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                );
                                                            })()}

                                                            {order.money_type === 'transfer' && order.payment_number_phone && (
                                                                <Box sx={{ mt: 0.2 }}>
                                                                    <Typography sx={{ fontSize: 10, color: '#818cf8', fontWeight: 600 }}>
                                                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                                            {order.payment_number_bank && (
                                                                                <BankBadge bank={order.payment_number_bank} size={16}
                                                                                    logoUrl={order.payment_number_bank_logo_url ? `${API_URL}${order.payment_number_bank_logo_url}` : null}
                                                                                    color={order.payment_number_bank_color} />
                                                                            )}
                                                                            Перевод на: {order.payment_number_bank_display ? `${order.payment_number_bank_display} · ` : ''}{order.payment_number_phone}{order.payment_number_owner ? ` (${order.payment_number_owner})` : ''}
                                                                        </Box>
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            {order.items?.length > 0 && (
                                                                <Box sx={{ mt: 0.4, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {order.items.map((it, i) => (
                                                                        <Chip key={i} size="sm" variant="soft" sx={{
                                                                            fontSize: 10, fontWeight: 600,
                                                                            bgcolor: 'var(--joy-palette-background-level2)',
                                                                            color: 'var(--joy-palette-text-secondary)',
                                                                        }}>
                                                                            {it.product_name_snapshot}{it.quantity > 1 ? ` × ${it.quantity}` : ''}
                                                                        </Chip>
                                                                    ))}
                                                                </Box>
                                                            )}

                                                            {(order.photographer_comment || order.comment) && (
                                                                <Box sx={{ mt: 0.4, pl: 1, borderLeft: '2px solid #f59e0b' }}>
                                                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                        💬 {order.photographer_comment || order.comment}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>

                                                        {/* Тип оплаты */}
                                                        <Chip size="sm" variant="soft" sx={{
                                                            fontSize: 10, fontWeight: 700,
                                                            bgcolor: mc.bg, color: mc.color,
                                                            flexShrink: 0,
                                                        }}
                                                            startDecorator={MONEY_ICONS[order.money_type]}
                                                        >
                                                            {MONEY_LABELS[order.money_type]}
                                                        </Chip>

                                                        {/* Сумма */}
                                                        <Typography sx={{
                                                            fontSize: 15, fontWeight: 800,
                                                            color: 'var(--joy-palette-text-primary)',
                                                            minWidth: 60, textAlign: 'right',
                                                            fontVariantNumeric: 'tabular-nums',
                                                        }}>
                                                            {fmtMoney(order.value)}
                                                        </Typography>

                                                        {/* Удалить */}
                                                        {canDelete && (
                                                            <Tooltip title="Удалить чек" size="sm">
                                                                <IconButton
                                                                    size="sm" variant="plain"
                                                                    onClick={() => setDeleteTarget(order)}
                                                                    sx={{
                                                                        color: 'var(--joy-palette-text-tertiary)',
                                                                        '&:hover': { color: 'var(--joy-palette-danger-400)' },
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    <DeleteRoundedIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                </motion.div>
                            ))}
                        </Stack>
                    </motion.div>
                )}
            </Box>

            {/* ── Модалка удаления ───────────────────────────────── */}
            <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)',
                    maxWidth: 380,
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Удалить чек?
                    </Typography>
                    {deleteTarget && (
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                            {deleteTarget.photographer_name} — {fmtMoney(deleteTarget.value)} ₽ ({MONEY_LABELS[deleteTarget.money_type]})
                            за {fmtDate(deleteTarget.date)}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" color="neutral" size="sm"
                            onClick={() => setDeleteTarget(null)}>
                            Отмена
                        </Button>
                        <Button color="danger" size="sm" loading={deleting}
                            onClick={handleDelete}>
                            Удалить
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}